/** @format */

import * as Sentry from "@sentry/react";
import type { ColumnTable } from "arquero";
import * as aq from "arquero";
import axios, {
  type AxiosError,
  type AxiosRequestConfig,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";

import { safeStorage } from "@/utils/storage";

// Per-request opt-out from recursive snake↔camel conversion. Naming the
// option here (rather than inline-casting at every call site) keeps the
// public typing of `galaxieClient.get/post/...` consistent for callers.
declare module "axios" {
  interface AxiosRequestConfig {
    opaqueValueKeys?: ReadonlySet<string>;
  }
}

// Enhanced error classes for better error handling
export class NetworkError extends Error {
  type = "network" as const;
  originalError: unknown;

  constructor(message: string, originalError: unknown) {
    super(message);
    this.name = "NetworkError";
    this.originalError = originalError;
  }
}

export class TimeoutError extends Error {
  type = "timeout" as const;
  timeout: number;

  constructor(message: string, timeout: number) {
    super(message);
    this.name = "TimeoutError";
    this.timeout = timeout;
  }
}

export class HttpError extends Error {
  type = "http" as const;
  status: number;
  statusText: string;
  data: unknown;

  constructor(message: string, status: number, statusText: string, data: unknown) {
    super(message);
    this.name = "HttpError";
    this.status = status;
    this.statusText = statusText;
    this.data = data;
  }
}

/**
 * Extract a short, human-readable error detail from a response body so error
 * messages (and Sentry grouping) reflect the real cause rather than the
 * generic "Unknown error" statusText fallback.
 */
function extractErrorDetail(data: unknown): string | null {
  if (typeof data === "string") return data.slice(0, 200) || null;
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  // DRF conventions: { detail: string } or { error: string } or
  // { non_field_errors: string[] } (camelCased to nonFieldErrors).
  for (const key of ["detail", "error", "message", "nonFieldErrors"]) {
    const value = record[key];
    if (typeof value === "string" && value) return value.slice(0, 200);
    if (Array.isArray(value) && typeof value[0] === "string")
      return value[0].slice(0, 200);
  }
  // Fallback: first string-or-string-array field (DRF field-level errors).
  for (const value of Object.values(record)) {
    if (typeof value === "string" && value) return value.slice(0, 200);
    if (Array.isArray(value) && typeof value[0] === "string")
      return value[0].slice(0, 200);
  }
  return null;
}

export type GalaxieApiError = NetworkError | TimeoutError | HttpError;

// Type guard functions
export const isNetworkError = (error: unknown): error is NetworkError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "type" in error &&
    (error as { type: string }).type === "network"
  );
};

export const isTimeoutError = (error: unknown): error is TimeoutError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "type" in error &&
    (error as { type: string }).type === "timeout"
  );
};

export const isHttpError = (error: unknown): error is HttpError => {
  return (
    typeof error === "object" &&
    error !== null &&
    "type" in error &&
    (error as { type: string }).type === "http"
  );
};

/**
 * True for request outcomes that are expected/transient rather than actionable
 * application bugs, and therefore should not be captured as Sentry exceptions:
 *  - NetworkError / TimeoutError: the user is offline or the connection is slow
 *    (JAVASCRIPT-REACT-FM / ES).
 *  - HTTP 404: the target resource was deleted or is stale — e.g. forking a
 *    RunView that no longer exists (JAVASCRIPT-REACT-F1).
 *
 * This is request-outcome classification at the point of handling, not a
 * blanket beforeSend mute — genuine 4xx/5xx bugs are still reported.
 */
export const isExpectedRequestError = (error: unknown): boolean =>
  isNetworkError(error) ||
  isTimeoutError(error) ||
  (isHttpError(error) && error.status === 404);

export const ARROW_MIME_TYPES = [
  "application/vnd.apache.arrow.stream",
  "application/vnd.apache.arrow.file",
] as const;

const isArrowContentType = (ct?: string) =>
  !!ct && ARROW_MIME_TYPES.some(m => ct.includes(m));

export const ARROW_MIME_TYPE = "application/vnd.apache.arrow.stream";

// Token management helpers
const TOKEN_KEYS = {
  ACCESS: "accessToken",
  REFRESH: "refreshToken",
} as const;

/**
 * Gets the access token from storage (SSR-safe)
 * @returns The access token or null if not found
 */
export const getAccessToken = (): string | null => {
  return safeStorage.getItem(TOKEN_KEYS.ACCESS);
};

/**
 * Gets the refresh token from storage (SSR-safe)
 * @returns The refresh token or null if not found
 */
export const getRefreshToken = (): string | null => {
  return safeStorage.getItem(TOKEN_KEYS.REFRESH);
};

/**
 * Sets both access and refresh tokens in storage (SSR-safe)
 * @param access - The access token
 * @param refresh - The refresh token
 */
export const setTokens = (access: string, refresh: string): void => {
  safeStorage.setItem(TOKEN_KEYS.ACCESS, access);
  safeStorage.setItem(TOKEN_KEYS.REFRESH, refresh);
};

/**
 * Removes both access and refresh tokens from storage (SSR-safe)
 */
export const clearTokens = (): void => {
  safeStorage.removeItem(TOKEN_KEYS.ACCESS);
  safeStorage.removeItem(TOKEN_KEYS.REFRESH);
};

/**
 * Check if a refresh error is a definitive 401 from our Django backend
 * (not a transient proxy/CDN 401). SimpleJWT returns { detail: "...", code: "token_not_valid" }.
 */
const isDefinitiveAuthRejection = (error: unknown): boolean => {
  if (!isHttpError(error) || error.status !== 401) return false;
  const data = error.data as Record<string, unknown> | null;
  return data != null && typeof data.detail === "string";
};

// Token refresh queue to prevent race conditions
let isRefreshing = false;
let refreshPromise: Promise<{ access: string; refresh: string }> | null = null;

// Get API base URL from environment or use default
// In SSR, use API_BASE_URL env var or default to relative path
const getApiBaseURL = (): string => {
  if (typeof window === "undefined") {
    // SSR context - use environment variable or default
    return process?.env?.API_BASE_URL ?? "http://server:8000/api/";
  }
  // Client context - use relative path
  return "/api/";
};

const galaxieClient = axios.create({
  baseURL: getApiBaseURL(),
  withCredentials: true,
  timeout: 1000 * 30,
  responseType: "json",
  responseEncoding: "utf8",
  headers: {
    "Content-Type": "application/json",
    Accept:
      "application/json, application/vnd.apache.arrow.stream, application/vnd.apache.arrow.file",
    // Cache-Control removed - let Cloudflare handle caching based on server headers
    // Only add no-cache for specific endpoints that shouldn't be cached (auth, mutations)
  },
  xsrfCookieName: "csrftoken",
  xsrfHeaderName: "X-CSRFToken",
  // Explicitly configure validateStatus to ensure proper error handling
  // Status codes outside this range will trigger the error interceptor
  validateStatus: status => status >= 200 && status < 300,
});

// Improved camelCase and snake_case converters that better handle numeric digits in keys

// Helper: camelCase a string, preserving digits as boundaries
function camelCaseKey(key: string): string {
  // Skip conversion for keys that look like data identifiers (e.g., "1-2026", "uuid-string")
  // These are dictionary keys that should not be transformed
  if (/^[\d]+-[\d]+$/.test(key)) {
    return key; // Keep "1-2026" style keys unchanged
  }

  // Replace _ or - followed by a letter or digit with the uppercase letter/digit
  // e.g. "stage_1_finish" -> "stage1Finish"
  return key.replace(/([-_][a-zA-Z0-9])/g, group => group[1].toUpperCase());
}

// Helper: snake_case a string, inserting _ before uppercase letters and before digits that follow a letter
function snakeCaseKey(key: string): string {
  // Special case for password fields with numbers - don't add underscore between password and number
  if (key.match(/^password\d+$/i)) {
    return key.toLowerCase();
  }

  // Special case for email1 field - convert to email (singular)
  if (key === "email1") {
    return "email";
  }

  // Special case for p256dh fields (Web Push VAPID keys) - don't add underscores around 256
  // p256dhKey -> p256dh_key (not p_256_dh_key)
  if (key.match(/^p256dh/i)) {
    return key.toLowerCase().replace(/key$/i, "_key");
  }

  // Insert _ before uppercase letters, then lowercase everything
  // Also insert _ before digits that follow a letter (e.g. "stage1Finish" -> "stage_1_finish")
  return key
    .replace(/([a-z])([A-Z])/g, "$1_$2") // fooBar -> foo_Bar
    .replace(/([a-zA-Z])([0-9]+)/g, "$1_$2") // foo1 -> foo_1
    .replace(/([0-9]+)([a-zA-Z])/g, "$1_$2") // 1foo -> 1_foo
    .toLowerCase();
}

const EMPTY_KEY_SET: ReadonlySet<string> = new Set<string>();

// Per-request opt-out for the recursive case-conversion pass. When a key
// (matched against the camelCase form — i.e. how callers refer to the
// field on the frontend) is in the set, the value is passed through
// verbatim instead of recursed into. Used for opaque blobs whose internal
// structure is not Django-style snake_case (e.g. Perspective viewer
// configs that mix snake-case Datagrid keys with camelCase Intl options).
export const toCamelCase = (
  data: unknown,
  opaqueValueKeys: ReadonlySet<string> = EMPTY_KEY_SET
): unknown => {
  if (Array.isArray(data)) {
    return data.map(item => toCamelCase(item, opaqueValueKeys));
  } else if (data !== null && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    return Object.fromEntries(
      Object.keys(obj).map(key => {
        const camel = camelCaseKey(key);
        return [
          camel,
          opaqueValueKeys.has(camel)
            ? obj[key]
            : toCamelCase(obj[key], opaqueValueKeys),
        ];
      })
    );
  }
  return data;
};

export const toSnakeCase = (
  data: unknown,
  opaqueValueKeys: ReadonlySet<string> = EMPTY_KEY_SET
): unknown => {
  if (Array.isArray(data)) {
    return data.map(item => toSnakeCase(item, opaqueValueKeys));
  } else if (data !== null && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    return Object.fromEntries(
      Object.keys(obj).map(key => [
        snakeCaseKey(key),
        // Match against the input (camelCase) key so callers can name fields
        // by the same identifier on both directions.
        opaqueValueKeys.has(key) ? obj[key] : toSnakeCase(obj[key], opaqueValueKeys),
      ])
    );
  }
  return data;
};

// Request interceptor: attach JWT token dynamically and snakeCase POST data (for django)
galaxieClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Track request start time for performance monitoring
    (
      config as InternalAxiosRequestConfig & { metadata?: { startTime: number } }
    ).metadata = {
      startTime: Date.now(),
    };

    // Dynamically attach access token from localStorage on each request
    // This ensures the token is always current, even if it changes after module initialization
    const accessToken = getAccessToken();
    if (accessToken) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // Convert request data to snake_case for Django backend
    if (
      (config.method === "post" ||
        config.method === "put" ||
        config.method === "patch") &&
      config.data &&
      !(config.data instanceof FormData) // Skip FormData to preserve multipart data
    ) {
      config.data = toSnakeCase(config.data, config.opaqueValueKeys);
    }
    return config;
  },
  error => {
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
);

galaxieClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Calculate request duration and send to Sentry
    const config = response.config as InternalAxiosRequestConfig & {
      metadata?: { startTime: number };
    };
    if (!import.meta.env.DEV && config.metadata?.startTime) {
      const duration = Date.now() - config.metadata.startTime;

      // Log slow API requests (> 1 second) as breadcrumbs
      if (duration > 1000) {
        const endpoint = config.url ?? "unknown";
        Sentry.addBreadcrumb({
          category: "api",
          message: `Slow API: ${config.method?.toUpperCase() ?? "GET"} ${endpoint}`,
          level: "warning",
          data: { duration, status: response.status, endpoint },
        });
      }
    }

    const responseType = response.config.responseType;
    const contentType = response.headers["content-type"] as string | undefined;

    // Validate that the response content type is one of the allowed types
    if (contentType) {
      const isJson = contentType.includes("application/json");
      const isArrow = isArrowContentType(contentType);
      if (!isJson && !isArrow) {
        return Promise.reject(
          new HttpError(
            `Unsupported content type: ${contentType}`,
            response.status,
            response.statusText,
            response.data
          )
        );
      }
    }

    if (isArrowContentType(contentType)) {
      response.data = parseArrowResponse(response);
    }
    if (responseType === "json") {
      response.data = toCamelCase(response.data, config.opaqueValueKeys);
    }
    return response;
  },
  async (rawError: unknown) => {
    // Bail early for non-Axios errors (e.g., thrown in success interceptor)
    if (!axios.isAxiosError(rawError)) {
      return Promise.reject(
        rawError instanceof Error ? rawError : new Error(String(rawError))
      );
    }
    const error = rawError;
    const axiosError = error as AxiosError & {
      config?: InternalAxiosRequestConfig & { _retry?: boolean };
    };
    const originalRequest = axiosError?.config;
    const timeout = originalRequest?.timeout ?? galaxieClient.defaults.timeout ?? 30000;

    // Handle timeout errors specifically
    if (error.code === "ECONNABORTED" && error.message.includes("timeout")) {
      const timeoutError = new TimeoutError(
        `Request timed out after ${timeout}ms`,
        timeout
      );
      // Record timeout as breadcrumb — infrastructure noise, not an application bug
      Sentry.addBreadcrumb({
        category: "http",
        message: `Request timed out after ${timeout}ms`,
        level: "warning",
        data: { url: originalRequest?.url, timeout },
      });
      return Promise.reject(timeoutError);
    }

    // Handle network errors (no response received)
    if (error.request && !error.response) {
      const networkError = new NetworkError(
        "Network error: No response received from server",
        error
      );
      // Record network error as breadcrumb — transient connectivity, not an application bug
      Sentry.addBreadcrumb({
        category: "http",
        message: "Network error: No response received from server",
        level: "warning",
        data: { url: originalRequest?.url },
      });
      return Promise.reject(networkError);
    }

    // Handle HTTP errors (response received with error status)
    if (error.response) {
      // Convert error response data to camelCase before creating error
      let errorData = error.response.data;
      if (errorData) {
        error.response.data = toCamelCase(
          error.response.data,
          originalRequest?.opaqueValueKeys
        );
        errorData = error.response.data;
      }

      const detail =
        extractErrorDetail(errorData) || error.response.statusText || "Unknown error";
      const httpError = new HttpError(
        `HTTP ${error.response.status}: ${detail}`,
        error.response.status,
        error.response.statusText || "Unknown error",
        errorData
      );

      // Handle 401 Unauthorized errors with token refresh
      if (error.response.status === 401 && originalRequest && !originalRequest._retry) {
        const refreshToken = getRefreshToken();
        const isRefreshEndpoint = axiosError?.config?.url === "/token/refresh/";

        // Attempt token refresh if we have a refresh token and this isn't the refresh endpoint
        if (refreshToken && !isRefreshEndpoint) {
          originalRequest._retry = true;

          // Use shared refresh promise to prevent race conditions
          // If a refresh is already in progress, wait for it to complete
          if (!isRefreshing) {
            isRefreshing = true;

            const attemptRefresh = async (
              retriesLeft: number
            ): Promise<{ access: string; refresh: string }> => {
              try {
                const response = await galaxieClient.post(
                  "/token/refresh/",
                  { refresh: refreshToken },
                  {
                    headers: { "Cache-Control": "no-cache, no-store" },
                    _retry: true, // Skip 401 interceptor — we handle refresh errors here
                  } as AxiosRequestConfig & { _retry: boolean }
                );
                const { access, refresh: newRefresh } = response.data as {
                  access: string;
                  refresh: string;
                };
                setTokens(access, newRefresh);
                return { access, refresh: newRefresh };
              } catch (error: unknown) {
                if (isDefinitiveAuthRejection(error)) {
                  // Backend explicitly rejected our refresh token — log out
                  clearTokens();
                  throw error;
                }
                if (retriesLeft > 0) {
                  // Transient failure (network, proxy 401, 5xx) — retry after delay
                  await new Promise(r => setTimeout(r, 1000));
                  return attemptRefresh(retriesLeft - 1);
                }
                // Exhausted retries — don't clear tokens, let request fail
                // User stays logged in; next request will try refreshing again
                throw error;
              }
            };

            refreshPromise = attemptRefresh(1).finally(() => {
              isRefreshing = false;
              refreshPromise = null;
            });
          }

          try {
            // Wait for the shared refresh promise to complete
            await refreshPromise;
            // Retry original request with new token
            // The request interceptor will automatically attach the new token
            return await galaxieClient(originalRequest as AxiosRequestConfig);
          } catch (refreshError: unknown) {
            throw refreshError instanceof Error
              ? refreshError
              : new Error(String(refreshError));
          }
        } else if (!refreshToken) {
          // No refresh token available — can't refresh, clear stale access token
          clearTokens();
        }
      }

      // Log non-auth errors to Sentry (excluding auth/user endpoints and 401s)
      // 401s are expected when users aren't authenticated - don't pollute error tracking
      // Fixes JAVASCRIPT-REACT-E4: HTTP 401 errors being reported unnecessarily
      if (axiosError?.config?.url !== "/auth/user/" && error.response.status !== 401) {
        const extraData: Record<string, unknown> = {
          status: error.response.status,
          statusText: error.response.statusText,
          url: axiosError?.config?.url,
          method: axiosError?.config?.method,
        };

        if (error.response.status >= 500) {
          // 5xx errors are server/infrastructure issues — breadcrumb only
          Sentry.addBreadcrumb({
            category: "http",
            message: `HTTP ${error.response.status}: ${error.response.statusText || "Server Error"}`,
            level: "warning",
            data: extraData,
          });
        } else {
          // 4xx errors (except 401) indicate real client-side bugs — capture as exceptions
          if (error.response.status === 400) {
            extraData.requestData = originalRequest?.data;
            extraData.requestParams = originalRequest?.params;
            extraData.responseData = errorData;
          }

          Sentry.captureException(httpError, {
            tags: { errorType: "http", statusCode: error.response.status },
            extra: extraData,
          });
        }
      }

      return Promise.reject(httpError);
    }

    // Handle other errors (shouldn't normally reach here) — breadcrumb only
    Sentry.addBreadcrumb({
      category: "http",
      message: `Unknown axios error: ${error.message}`,
      level: "warning",
    });
    return Promise.reject(error instanceof Error ? error : new Error(String(error)));
  }
);

/**
 * Parses Apache Arrow responses from PandasArrowRenderer and converts them to Arquero ColumnTable.
 * Prefers Arquero's fromArrow method for zero-copy import when available.
 * Expects callers to pass `{ responseType: 'arraybuffer' }` for Arrow endpoints.
 *
 * @param response - The Axios response object
 * @returns Arquero ColumnTable with camelCase column names
 * @throws Error if parsing fails or response is not Arrow format
 *
 * @example
 * ```typescript
 * // Using with Axios
 * const response = await galaxieClient.get('/api/data/', { responseType: 'arraybuffer' });
 * const arqueroTable = parseArrowResponse(response);
 *
 * // Convert to records if needed
 * const records = arqueroTableToRecords(arqueroTable);
 * ```
 */
export const parseArrowResponse = (
  response: Pick<AxiosResponse<ArrayBuffer>, "data" | "headers">
): ColumnTable => {
  const contentType = response.headers["content-type"] as string | undefined;

  if (!isArrowContentType(contentType)) {
    throw new Error(
      `Response is not Apache Arrow format. Content-Type: ${contentType}`
    );
  }
  const arqueroTable = aq.fromArrow(new Uint8Array(response.data), {
    useProxy: true, // zero-copy row proxies when possible
  });

  // Rename columns to camelCase
  const renameMap: Record<string, string> = {};
  arqueroTable.columnNames().forEach((colName: string) => {
    renameMap[colName] = camelCaseKey(colName);
  });

  return arqueroTable.rename(renameMap);
};

/**
 * Duck-type guard for an Arquero ColumnTable.
 *
 * The Arrow endpoints are converted to a ColumnTable by the response
 * interceptor. If a non-Arrow response slips through (error body, empty payload,
 * wrong content-type) `res.data` is a raw ArrayBuffer, and casting it
 * `as ColumnTable` yielded a fake "table" that later crashed with
 * "f.objects is not a function" (JAVASCRIPT-REACT-H9 / H8 / ED). Validate the
 * shape before treating a value as a table.
 */
export const isColumnTable = (v: unknown): v is ColumnTable =>
  v != null &&
  typeof (v as ColumnTable).objects === "function" &&
  typeof (v as ColumnTable).numRows === "function";

// Helper to rename all Arquero column names to camelCase on an existing table
// Note: This only converts column names, not nested data. Use arqueroTableToRecords()
// for full camelCase conversion including nested objects and lists.
export const camelCaseArqueroTable = (table: ColumnTable): ColumnTable => {
  const renameMap: Record<string, string> = {};
  table.columnNames().forEach((colName: string) => {
    renameMap[colName] = camelCaseKey(colName);
  });
  return table.rename(renameMap);
};

/**
 * Utility function to convert Arquero ColumnTable to array of objects (similar to pandas DataFrame.to_dict('records'))
 *
 * @template T - The type of the records to return (defaults to Record<string, unknown>)
 * @param table - Arquero ColumnTable
 * @returns Array of objects of type T representing the table data
 * @throws Error if invalid table is provided
 *
 * @example
 * ```typescript
 * interface VehicleData {
 *   vehicleId: string;
 *   lapNumber: number;
 *   lapTime: number;
 * }
 *
 * const records = arqueroTableToRecords<VehicleData>(arqueroTable);
 * // records is now typed as VehicleData[]
 * ```
 */
export const arqueroTableToRecords = <T = Record<string, unknown>>(
  table: ColumnTable
): T[] => {
  if (!table?.objects) {
    throw new Error("Invalid Arquero table provided");
  }

  // Use the object method to convert to records
  return table.objects() as T[];
};

export default galaxieClient;
